import { I18n } from '@calendly/i18n';
import { IconSettings } from '@calendly/ui/components/glyphs/IconSettings';
import { IconSquareRoundedArrowUp } from '@calendly/ui/components/glyphs/IconSquareRoundedArrowUp';
import { IconX } from '@calendly/ui/components/glyphs/IconX';
import { IconButton } from '@calendly/ui/components/icon-button';
import { NavFooter, NavHeader, NavItem, NavItemsList, VerticalNav } from '@calendly/ui/components/navigation';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorIconActionContrast, colorIconStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { type FC, type MouseEvent, type ReactNode, useMemo } from 'react';
import { NavigationWrapper } from 'features/left_navigation/elements';
import { navigationAnalyticsIdForLeftNavSettings, trackLeftNavSettingsClick, useTrack } from 'hooks/analytics_events';
import { CollapsedAvatarRow, ExpandedAvatarRow } from './avatar_row';
import { ProfileMenuContent } from './avatar_row/ProfileMenuContent';
import type { MainNavAvatarRowProfile } from './avatar_row/types';
import { createShellNavAnchorClickHandler } from './shellNavLinkClick';
import { pickSelectedHref } from './shellNavSelection';
import type { ShellNavRowDef } from './shellVerticalNavIcons';
type History = {
  push: (path: string) => void;
};

/**
 * Typed shim for `NavigationWrapper`: the legacy navigation shell’s outer click/interaction wrapper (JS `forwardRef`).
 */
const ShellNavigationWrapper = NavigationWrapper as FC<{
  children: ReactNode;
  onClick?: () => void;
}>;
const navShellCss = "n102h6uo";
const navHeaderCss = "nd1lu4k";
const expandedNavHeaderCss = "e11xopav";
const collapsedNavHeaderCss = "cqq4v43";

/**
 * Props for the presentational shell vertical nav (`AppShellVerticalNavView`).
 * Wired by `useAppShellVerticalNavModel` in-app; Storybook supplies mocks.
 */
export type AppShellVerticalNavViewProps = {
  profile: MainNavAvatarRowProfile;
  history: History;
  pathname: string;
  topRows: ShellNavRowDef[];
  showFooterUpgradeNavItem: boolean;
  /** Destination for the footer Settings row (`useProductRefreshSettingsIa`: modal query on current path; else legacy profile). */
  footerSettingsHref: string;
  onShellNavInteraction: () => void;
  onFooterUpgradePlan: () => void;
  /**
   * Mobile / overlay: CUI rail uses zero-width collapsed geometry (`NavBar` **`fullExpandAndCollapse`**) instead of the
   * desktop minimized rail. Pair with **`expandable: false`** inside this view.
   */
  fullExpandAndCollapse?: boolean;
  /** Header close control for the mobile drawer; independent of {@link fullExpandAndCollapse}. */
  onClose?: () => void;
};

/**
 * CUI vertical rail only: paths, selection, Sprig wrapper clicks, and footer actions come from props.
 * Keeps Redux, RTK, Sprig, and billing hooks in `useAppShellVerticalNavModel`.
 */
export const AppShellVerticalNavView: FC<AppShellVerticalNavViewProps> = ({
  profile,
  history,
  pathname,
  topRows,
  showFooterUpgradeNavItem,
  footerSettingsHref,
  onShellNavInteraction,
  onFooterUpgradePlan,
  fullExpandAndCollapse = false,
  onClose
}) => {
  const track = useTrack();
  const selectedItemId = pickSelectedHref(pathname, topRows);
  const handleFooterSettingsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    trackLeftNavSettingsClick(track, pathname);
    createShellNavAnchorClickHandler(history, footerSettingsHref)(event);
  };
  const profileMenu = useMemo(() => <ProfileMenuContent fullName={profile.fullName} businessName={profile.businessName} planLabel={profile.planLabel} showAccountMenuUpgrade={profile.showAccountMenuUpgrade} isSuperAdmin={profile.isSuperAdmin} />, [profile.fullName, profile.businessName, profile.planLabel, profile.showAccountMenuUpgrade, profile.isSuperAdmin]);
  const renderNavRows = (rows: ShellNavRowDef[]) => rows.map(row => {
    if (!row.shouldShow) return null;
    const Idle = row.iconIdle;
    const Selected = row.iconSelected ?? Idle;
    if (!Idle) {
      throw new Error(`AppShellVerticalNav: row "${row.label}" (${row.href}) is missing iconIdle.`);
    }
    return <NavItem key={row.href} href={row.href} itemId={row.href} label={row.label} icon={<UiIcon color={colorIconStandard} icon={Idle as never} size="medium" title="" />} selectedIcon={Selected !== Idle ? <UiIcon color={colorIconActionContrast} icon={Selected as never} size="medium" title="" /> : undefined} suffix={row.suffix} data-calendly-label={row.dataCalendlyLabel} data-pendo-id={row.pendoId} onClick={createShellNavAnchorClickHandler(history, row.href)} />;
  });
  return <VerticalNav className={navShellCss} defaultState="expanded" expandable={!fullExpandAndCollapse} fullExpandAndCollapse={fullExpandAndCollapse} navHeader={<NavHeader className={classNames(navHeaderCss, expandedNavHeaderCss)}>
          <ExpandedAvatarRow profile={profile} profileMenu={profileMenu} compactExpandedAvatarRow={fullExpandAndCollapse && Boolean(onClose)} />
          {onClose && <IconButton title={I18n.t('publisher.app_shell.app_bar.close')} onClick={onClose}>
              <UiIcon icon={IconX} size="medium" title="" />
            </IconButton>}
        </NavHeader>} collapsedNavHeader={<NavHeader className={classNames(navHeaderCss, collapsedNavHeaderCss)}>
          <CollapsedAvatarRow profile={profile} profileMenu={profileMenu} />
        </NavHeader>} selectedItemId={selectedItemId} toggleCollapseLabel={I18n.t('publisher.left_navigation.collapse')} toggleExpandLabel={I18n.t('publisher.left_navigation.expand')} footer={<NavFooter>
          <ShellNavigationWrapper onClick={onShellNavInteraction}>
            <NavItemsList>
              {showFooterUpgradeNavItem ? <NavItem key="shell-nav-upgrade-plan" label={I18n.t('publisher.left_navigation.upgrade_cta.caption')} icon={<UiIcon color={colorIconStandard} icon={IconSquareRoundedArrowUp as never} size="medium" title="" />} onClick={onFooterUpgradePlan} /> : null}
              {/*
               * CUI `NavItem` uses `itemId` for selection only; it is not written to the DOM. Product analytics and
               * tests key off a stable `data-item-id` on the anchor.
               */}
              <NavItem key="shell-nav-settings" href={footerSettingsHref} itemId="shell-nav-settings" label={I18n.t('publisher.meetings.settings')} icon={<UiIcon color={colorIconStandard} icon={IconSettings as never} size="medium" title="" />} data-item-id="shell-nav-settings" data-analytics-id={navigationAnalyticsIdForLeftNavSettings()} onClick={handleFooterSettingsClick} />
            </NavItemsList>
          </ShellNavigationWrapper>
        </NavFooter>}>
      <ShellNavigationWrapper onClick={onShellNavInteraction}>
        <NavItemsList>
          {renderNavRows(topRows)}
        </NavItemsList>
      </ShellNavigationWrapper>
    </VerticalNav>;
};

require("./AppShellVerticalNavView.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AppShellVerticalNavView.tsx");